import { Plus, Eye, EyeOff, Settings, Trash2 } from 'lucide-react';
import type { Announcement } from '../../services/api';

interface AnnouncementsTabProps {
  announcements: Announcement[];
  onAdd: () => void;
  onEdit: (announcement: Announcement) => void;
  onDelete: (id: string) => void;
  onToggle: (id: string, isVisible: boolean) => void;
}

export default function AnnouncementsTab({
  announcements,
  onAdd,
  onEdit,
  onDelete,
  onToggle,
}: AnnouncementsTabProps) {
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-light-text">公告管理</h2>
          <p className="text-sm text-light-muted">管理网站公告和通知</p>
        </div>
        <button
          onClick={onAdd}
          className="bg-accent-blue text-white font-medium px-4 py-2 rounded-xl hover:shadow-lg hover:scale-[1.02] active:scale-[0.98] transition-all duration-200 flex items-center gap-2"
        >
          <Plus className="w-4 h-4" />
          添加公告
        </button>
      </div>

      {/* Announcement List */}
      <div className="bg-white rounded-2xl shadow-lg border border-light-border overflow-hidden">
        {/* Desktop Table */}
        <div className="hidden lg:block overflow-x-auto">
          <table className="w-full">
            <thead className="bg-light-hover">
              <tr>
                <th className="px-4 py-3 text-left text-sm font-medium text-light-text">标签</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-light-text">标题</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-light-text">类型</th>
                <th className="px-4 py-3 text-center text-sm font-medium text-light-text">可见性</th>
                <th className="px-4 py-3 text-center text-sm font-medium text-light-text">状态</th>
                <th className="px-4 py-3 text-center text-sm font-medium text-light-text">操作</th>
              </tr>
            </thead>
            <tbody>
              {announcements.map((announcement) => (
                <tr key={announcement.id} className="border-t border-light-border">
                  <td className="px-4 py-3">
                    <span className={`px-2 py-1 rounded-lg text-xs ${
                      announcement.type === 'warning'
                        ? 'bg-yellow-100 text-yellow-600'
                        : 'bg-blue-100 text-blue-600'
                    }`}>
                      {announcement.label}
                    </span>
                  </td>
                  <td className="px-4 py-3 max-w-xs truncate">
                    <span className="text-sm text-light-text">{announcement.title}</span>
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-sm text-light-text capitalize">{announcement.type}</span>
                  </td>
                  <td className="px-4 py-3 text-center">
                    <span className={`px-2 py-1 rounded-lg text-xs ${
                      announcement.isLoginRequired
                        ? 'bg-purple-100 text-purple-600'
                        : 'bg-blue-100 text-blue-600'
                    }`}>
                      {announcement.isLoginRequired ? '登录后' : '公共'}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-center">
                    <span className={`px-2 py-1 rounded-lg text-xs ${
                      announcement.isVisible
                        ? 'bg-green-100 text-green-600'
                        : 'bg-gray-100 text-gray-600'
                    }`}>
                      {announcement.isVisible ? '显示' : '隐藏'}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-center gap-2">
                      <button
                        onClick={() => onToggle(announcement.id, announcement.isVisible)}
                        className={`p-2 rounded-lg transition-colors ${
                          announcement.isVisible
                            ? 'text-green-600 hover:bg-green-50'
                            : 'text-gray-600 hover:bg-gray-50'
                        }`}
                        title={announcement.isVisible ? '隐藏' : '显示'}
                      >
                        {announcement.isVisible ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                      </button>
                      <button
                        onClick={() => onEdit(announcement)}
                        className="p-2 text-accent-blue hover:bg-blue-50 rounded-lg transition-colors"
                        title="编辑"
                      >
                        <Settings className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => onDelete(announcement.id)}
                        className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                        title="删除"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Mobile Cards */}
        <div className="lg:hidden divide-y divide-light-border">
          {announcements.map((announcement) => (
            <div key={announcement.id} className="p-4">
              {/* Header */}
              <div className="flex items-start justify-between gap-2 mb-2">
                <div className="flex flex-wrap items-center gap-2">
                  <span className={`px-2 py-1 rounded-lg text-xs ${
                    announcement.type === 'warning'
                      ? 'bg-yellow-100 text-yellow-600'
                      : 'bg-blue-100 text-blue-600'
                  }`}>
                    {announcement.label}
                  </span>
                  <span className="text-sm text-light-text">{announcement.title}</span>
                </div>
              </div>

              {/* Info Row */}
              <div className="flex items-center gap-2 mb-3">
                <span className="text-xs text-light-muted capitalize">{announcement.type}</span>
                <span className="text-light-muted">·</span>
                <span className={`px-2 py-0.5 rounded text-xs ${
                  announcement.isLoginRequired
                    ? 'bg-purple-100 text-purple-600'
                    : 'bg-blue-100 text-blue-600'
                }`}>
                  {announcement.isLoginRequired ? '登录后可见' : '公共可见'}
                </span>
                <span className="text-light-muted">·</span>
                <span className={`px-2 py-0.5 rounded text-xs ${
                  announcement.isVisible
                    ? 'bg-green-100 text-green-600'
                    : 'bg-gray-100 text-gray-600'
                }`}>
                  {announcement.isVisible ? '显示中' : '已隐藏'}
                </span>
              </div>

              {/* Actions */}
              <div className="flex gap-2">
                <button
                  onClick={() => onToggle(announcement.id, announcement.isVisible)}
                  className={`flex items-center gap-1 px-3 py-2 text-sm rounded-lg transition-colors ${
                    announcement.isVisible
                      ? 'text-green-600 bg-green-50 hover:bg-green-100'
                      : 'text-gray-600 bg-gray-50 hover:bg-gray-100'
                  }`}
                >
                  {announcement.isVisible ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                  {announcement.isVisible ? '隐藏' : '显示'}
                </button>
                <button
                  onClick={() => onEdit(announcement)}
                  className="flex items-center gap-1 px-3 py-2 text-sm text-accent-blue bg-blue-50 hover:bg-blue-100 rounded-lg transition-colors"
                >
                  <Settings className="w-4 h-4" />
                  编辑
                </button>
                <button
                  onClick={() => onDelete(announcement.id)}
                  className="flex items-center gap-1 px-3 py-2 text-sm text-red-600 bg-red-50 hover:bg-red-100 rounded-lg transition-colors"
                >
                  <Trash2 className="w-4 h-4" />
                  删除
                </button>
              </div>
            </div>
          ))}
          {announcements.length === 0 && (
            <div className="px-4 py-8 text-center text-light-muted">
              暂无公告，点击「添加公告」创建
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
